<template>
  <div class="authenticated-image" :class="wrapperClass">
    <v-img
      v-if="imageDataUrl"
      :src="imageDataUrl"
      :alt="alt"
      :max-height="maxHeight"
      :max-width="maxWidth"
      :contain="contain"
      :class="imageClass"
      v-bind="$attrs"
    />
    <div v-else-if="loading" class="d-flex justify-center align-center" :style="loadingStyle">
      <v-progress-circular size="24" color="primary" indeterminate />
    </div>
    <div v-else-if="error" class="d-flex flex-column justify-center align-center text-disabled" :style="errorStyle">
      <v-icon>mdi-image-broken</v-icon>
      <span class="mt-1 text-caption">Error al cargar imagen</span>
    </div>
  </div>
</template>

<script>
import { questionService } from '@/services/questionsApi'

export default {
  name: 'AuthenticatedImage',

  props: {
    src: {
      type: String,
      required: true
    },
    alt: {
      type: String,
      default: 'Imagen'
    },
    maxHeight: {
      type: [String, Number],
      default: undefined
    },
    maxWidth: {
      type: [String, Number],
      default: undefined
    },
    contain: {
      type: Boolean,
      default: true
    },
    imageClass: {
      type: String,
      default: ''
    },
    wrapperClass: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      imageDataUrl: null,
      loading: false,
      error: false,
      componentKey: 0 // For forcing re-renders if needed
    }
  },

  computed: {
    loadingStyle() {
      return {
        height: typeof this.maxHeight === 'number' ? `${this.maxHeight}px` : this.maxHeight || '100px',
        width: typeof this.maxWidth === 'number' ? `${this.maxWidth}px` : this.maxWidth || '100%'
      }
    },

    errorStyle() {
      return this.loadingStyle
    }
  },

  watch: {
    src: {
      immediate: true,
      handler(newSrc, oldSrc) {
        if (newSrc && newSrc !== oldSrc) {
          // Reset state first
          this.imageDataUrl = null
          this.error = false
          this.loading = false
          // Then load new image
          this.loadImage()
        }
      }
    }
  },

  methods: {
    async loadImage() {
      if (!this.src) return

      // Check if it's already a data URL or blob URL
      if (this.src.startsWith('data:') || this.src.startsWith('blob:')) {
        this.imageDataUrl = this.src
        return
      }

      // Check if it's a question image that needs authentication
      if (!this.src.includes('/api/questions/images/')) {
        this.imageDataUrl = this.src
        return
      }

      // Extract encoded filename from URL
      const match = this.src.match(/\/api\/questions\/images\/(.+)$/)
      if (!match) {
        this.imageDataUrl = this.src // Fallback to original URL
        return
      }

      const encodedFilename = match[1]

      this.loading = true
      this.error = false

      try {
        const response = await questionService.getQuestionImage(encodedFilename)

        // The response should be a blob
        let blob
        if (response instanceof Blob) {
          blob = response
        } else {
          // If it's not a blob, try to create one
          blob = new Blob([response], { type: response.type || 'image/jpeg' })
        }

        this.imageDataUrl = URL.createObjectURL(blob)

      } catch (error) {
        console.error('Error loading authenticated image:', error)
        this.error = true

        // Try fallback to original URL (might work if permissions change)
        this.imageDataUrl = this.src
      } finally {
        this.loading = false
      }
    }
  },

  beforeUnmount() {
    // Clean up blob URL to prevent memory leaks
    if (this.imageDataUrl && this.imageDataUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.imageDataUrl)
    }
  }
}
</script>

<style scoped>
.authenticated-image {
  position: relative;
}
</style>